import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { ordersApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);

  const orderId = searchParams.get('order_id');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!token) {
        setStatus('error');
        return;
      }
      if (!orderId) {
        setStatus('error');
        return;
      }

      try {
        const { data } = await ordersApi.get(orderId);
        if (cancelled) return;
        setOrder(data);
        setStatus('success');
        await clearCart();
        if (window.trackPurchase) {
          window.trackPurchase({
            id: data.id,
            total: data.total,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading order:', error);
          setStatus('error');
        }
      }
    };

    load();
    return () => { cancelled = true; };
    // clearCart kommer från context; vi vill bara köra vid orderId/token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="checkout-success-loading">
        <div className="bg-white rounded-md shadow-sm p-12 text-center max-w-md">
          <Loader2 className="h-16 w-16 text-[#003B73] animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-black text-[#0F172A] mb-2">Hämtar din order...</h1>
          <p className="text-gray-500">Ett ögonblick.</p>
        </div>
      </div>
    );
  }

  if (status === 'success' && order) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="checkout-success-page">
        <div className="bg-white rounded-md shadow-sm p-12 text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-2xl font-black text-[#0F172A] mb-2">Tack för din beställning!</h1>
          <p className="text-gray-500 mb-6">
            Vi har tagit emot din order. Du får en bekräftelse via e-post. Betalning sker via ByggFynd enligt de instruktioner som följer (Kustom).
          </p>
          {order.total != null && (
            <div className="bg-gray-50 rounded-md p-4 mb-6 text-left">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Belopp:</span> {Number(order.total).toLocaleString('sv-SE')} SEK
              </p>
              {order.order_number && (
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-semibold">Ordernummer:</span> {order.order_number}
                </p>
              )}
              {order.id && (
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-semibold">Order-ID:</span> {order.id.slice(0, 8).toUpperCase()}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Link to="/min-profil"><Button className="w-full bg-[#003B73] hover:bg-[#002a52]">Visa mina ordrar</Button></Link>
            <Link to="/produkter"><Button className="w-full bg-[#FF8A00] hover:bg-[#e67c00]">Fortsätt handla</Button></Link>
            <Link to="/"><Button variant="outline" className="w-full">Tillbaka till startsidan</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="checkout-error">
      <div className="bg-white rounded-md shadow-sm p-12 text-center max-w-md">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="h-12 w-12 text-red-600" />
        </div>
        <h1 className="text-2xl font-black text-[#0F172A] mb-2">Något gick fel</h1>
        <p className="text-gray-500 mb-6">Vi kunde inte visa din order. Kontrollera att du är inloggad med samma konto som la beställningen.</p>
        <div className="flex flex-col gap-3">
          <Link to="/varukorg"><Button className="w-full bg-[#003B73] hover:bg-[#002a52]">Tillbaka till varukorgen</Button></Link>
          <Link to="/kontakt"><Button variant="outline" className="w-full">Kontakta oss</Button></Link>
        </div>
      </div>
    </div>
  );
};
